import React from "react";
import { Box } from "@mui/material";
import { format } from "date-fns";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import DownloadIcon from "../../svg/DownloadIcon";
import ClientBadge from "../common/ClientBadge";
import { PnoeMeasurementStatus } from "../../entities/pnoeMeasurement";
import { StatusTone } from "../../utils/labPanelStatus";
import { MeasurementActionKind } from "../../utils/pnoeMeasurementStatus";
import PnoeStatusSelect from "./PnoeStatusSelect";

/**
 * Header of the PNOĒ test detail page (Figma node 16827:67272).
 *
 * Replaces the old page title + back button row: the back link now lives in the
 * breadcrumb (`useBackNavigation`), so this card carries only what identifies
 * the test — status, type, date — plus the client it belongs to and the data
 * export.
 */
interface PnoeTestHeaderCardProps {
  /** The measurement's own status, for the select's current value. */
  status: PnoeMeasurementStatus | null | undefined;
  /** Composite status label, e.g. "Completed · Report ready". */
  statusLabel: string;
  statusTone: StatusTone;
  onStatusSelect: (action: MeasurementActionKind) => void;
  /** True while a transition is in flight. */
  statusBusy?: boolean;
  /** "RMR" / "AMR". */
  typeLabel: string;
  /** ISO start timestamp; rendered as "Jun 10 2026". */
  startedAt?: string | null;
  /**
   * Client name, when this page has to name its subject — only the
   * cross-client History page does; the workspace shells name it in the top
   * bar. Omitted means no badge and no divider.
   */
  clientName?: string | null;
  onExport: () => void;
  exporting?: boolean;
  /**
   * The customer's own view: the status is shown as a plain pill (no menu) and
   * the export button is not rendered.
   */
  readOnly?: boolean;
}

const PnoeTestHeaderCard: React.FC<PnoeTestHeaderCardProps> = ({
  status,
  statusLabel,
  statusTone,
  onStatusSelect,
  statusBusy = false,
  typeLabel,
  startedAt,
  clientName,
  onExport,
  exporting = false,
  readOnly = false,
}) => {
  const theme: Theme = useTheme();

  const startedLabel = startedAt
    ? format(new Date(startedAt), "MMM dd yyyy")
    : "";

  return (
    <Box sx={styles.container(theme)}>
      <Box sx={styles.left}>
        <PnoeStatusSelect
          status={status}
          label={statusLabel}
          tone={statusTone}
          onSelect={onStatusSelect}
          disabled={statusBusy}
          readOnly={readOnly}
        />

        {typeLabel && <Box sx={styles.typeBadge(theme)}>{typeLabel}</Box>}

        {startedLabel && <Box sx={styles.date(theme)}>{startedLabel}</Box>}
      </Box>

      <Box sx={styles.right}>
        {clientName && (
          <>
            <ClientBadge name={clientName} />
            <Box sx={styles.divider(theme)} />
          </>
        )}

        {!readOnly && (
          <Box
            sx={styles.exportButton(exporting)}
            onClick={() => {
              if (!exporting) onExport();
            }}
            role="button"
            aria-label="Export test data"
          >
            <DownloadIcon size={16} fill={theme.colors_ds.PRIMARY_ICON_MAIN} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PnoeTestHeaderCard;

const styles = {
  container: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    padding: "16px",
    borderRadius: "8px",
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    flexShrink: 0,
  }),
  left: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    minWidth: 0,
  },
  right: {
    display: "flex",
    alignItems: "center",
    alignSelf: "stretch",
    gap: "16px",
    flexShrink: 0,
  },
  // Measurement type. Neutral counterpart of the status pill — same metrics,
  // no dropdown.
  typeBadge: (theme: Theme) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "4px 12px",
    borderRadius: "8px",
    backgroundColor: theme.colorTokens.N[30],
    border: `1px solid ${theme.colors_ds.NEUTRAL_TEXT_WEAK}`,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    whiteSpace: "nowrap",
    flexShrink: 0,
  }),
  date: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "16px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    whiteSpace: "nowrap",
  }),
  divider: (theme: Theme) => ({
    width: "1px",
    alignSelf: "stretch",
    backgroundColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
    flexShrink: 0,
  }),
  exportButton: (exporting: boolean) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    flexShrink: 0,
    borderRadius: "8px",
    cursor: exporting ? "default" : "pointer",
    opacity: exporting ? 0.5 : 1,
    transition: "opacity 0.2s",
    "&:hover": exporting ? {} : { opacity: 0.7 },
  }),
};
